import React from "react";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get } from "lodash";
import moment from "moment";
import { detectFieldChanges, getMetaValue } from "utils/typeUtils";

import ComparisonCard from "./ComparisonCard";
import ApprovalCard from "./ApprovalCard";

// Backend admin-approval entity. PropTypes declared this as a loose `object`;
// it is only ever read via lodash `get(...)`, so model just the paths this
// component touches. Fields stay optional/loose to preserve runtime behavior.
interface KycApprovalMetadataItem {
  name?: string | null;
  value?: unknown;
}

interface KycApproval {
  correlationName?: string | null;
  entityIdentifier?: { id?: string | null } | null;
  metadataItems?: KycApprovalMetadataItem[] | null;
}

interface OwnProps {
  // Parent-passed props (propTypes minus `client`, which withApollo injects).
  approval?: KycApproval;
}

const formatDate = (value: moment.MomentInput) => {
  if (!value) return "N/A";

  const parsed = moment(value, "ddd MMM DD HH:mm:ss z YYYY");
  if (parsed.isValid()) {
    return parsed.format("MM/DD/YYYY");
  }
  const isoDate = moment(value);
  if (isoDate.isValid()) {
    return isoDate.format("MM/DD/YYYY");
  }
  return value;
};

const UserKycProfileUpdateApproval = ({
  approval,
}: WithApolloClient<OwnProps>) => {
  const metadataItems = get(approval, "metadataItems", []);
  const userEmail = get(approval, "correlationName", "");
  // getMetaValue returns `unknown` (the metadata `value` field); at runtime
  // these are string-or-null, which is how every consumer below treats them.
  const userId =
    (getMetaValue(metadataItems, "userId") as string | null) ||
    get(approval, "entityIdentifier.id", "");

  // SSN fields
  const currentSsn = getMetaValue(metadataItems, "Ssn/oldValue") as
    | string
    | null;
  const newSsn = getMetaValue(metadataItems, "Ssn/newValue") as string | null;
  const hasSsnChange = currentSsn || newSsn;

  // DOB fields - only show if dates are actually different (ignore time/timezone)
  const currentDobRaw = getMetaValue(metadataItems, "Dob/oldValue") as
    | string
    | null;
  const newDobRaw = getMetaValue(metadataItems, "Dob/newValue") as
    | string
    | null;
  const currentDob = formatDate(currentDobRaw);
  const newDob = formatDate(newDobRaw);
  const hasDobChange =
    currentDobRaw &&
    newDobRaw &&
    !moment(currentDobRaw).isSame(moment(newDobRaw), "day");

  // Address fields
  const currentAddress1 = getMetaValue(metadataItems, "Address1/oldValue") as
    | string
    | null;
  const newAddress1 = getMetaValue(metadataItems, "Address1/newValue") as
    | string
    | null;
  const currentAddress2 = getMetaValue(metadataItems, "Address2/oldValue") as
    | string
    | null;
  const newAddress2 = getMetaValue(metadataItems, "Address2/newValue") as
    | string
    | null;
  const currentCity = getMetaValue(metadataItems, "City/oldValue") as
    | string
    | null;
  const newCity = getMetaValue(metadataItems, "City/newValue") as string | null;
  const currentState = getMetaValue(metadataItems, "State/oldValue") as
    | string
    | null;
  const newState = getMetaValue(metadataItems, "State/newValue") as
    | string
    | null;
  const currentPostalCode = getMetaValue(
    metadataItems,
    "PostalCode/oldValue"
  ) as string | null;
  const newPostalCode = getMetaValue(metadataItems, "PostalCode/newValue") as
    | string
    | null;

  const hasAddressChange =
    (currentAddress1 !== newAddress1 && (currentAddress1 || newAddress1)) ||
    (currentAddress2 !== newAddress2 && (currentAddress2 || newAddress2)) ||
    (currentCity !== newCity && (currentCity || newCity)) ||
    (currentState !== newState && (currentState || newState)) ||
    (currentPostalCode !== newPostalCode &&
      (currentPostalCode || newPostalCode));

  // Build SSN changes
  const ssnChanges = hasSsnChange
    ? detectFieldChanges({ ssn: currentSsn }, { ssn: newSsn }, { ssn: "SSN" })
    : [];

  // Build DOB changes
  const dobChanges = hasDobChange
    ? detectFieldChanges(
        { dob: currentDob },
        { dob: newDob },
        { dob: "Date of Birth" }
      )
    : [];

  const currentAddress = {
    address1: currentAddress1 || "",
    address2: currentAddress2 || "",
    city: currentCity || "",
    state: currentState || "",
    postalCode: currentPostalCode || "",
  };

  const newAddress = {
    address1: newAddress1 || "",
    address2: newAddress2 || "",
    city: newCity || "",
    state: newState || "",
    postalCode: newPostalCode || "",
  };

  const addressFieldLabels = {
    address1: "Address Line 1",
    address2: "Address Line 2",
    city: "City",
    state: "State",
    postalCode: "Postal Code",
  };

  const addressChanges = hasAddressChange
    ? detectFieldChanges(currentAddress, newAddress, addressFieldLabels)
    : [];

  const formatAddress = (addr: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    postalCode: string;
  }) => {
    const parts = [
      addr.address1,
      addr.address2,
      [addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.join("\n") || "N/A";
  };

  // Build updated fields list based on actual changes
  const updatedFields = [
    hasSsnChange && "SSN",
    hasDobChange && "Date of Birth",
    hasAddressChange && "Address",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <ApprovalCard title="User Information">
        <div className="row">
          <div className="col-md-6">
            <p className="account-info-label">User ID</p>
            <p className="account-info-value">{userId || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <p className="account-info-label">User Email</p>
            <p className="account-info-value">{userEmail || "N/A"}</p>
          </div>
        </div>
        {updatedFields && (
          <div className="row" style={{ marginTop: 16 }}>
            <div className="col-md-12">
              <p className="account-info-label">Fields Being Updated</p>
              <p className="account-info-value">{updatedFields}</p>
            </div>
          </div>
        )}
      </ApprovalCard>

      {hasSsnChange && (
        <ComparisonCard
          title="SSN Comparison"
          currentLabel="Current SSN"
          newChangeLabel="New SSN"
          current={currentSsn || "N/A"}
          newChange={newSsn || "N/A"}
          changes={ssnChanges}
        />
      )}

      {/* formatDate returns a formatted string or passes its MomentInput value
          through; currentDob/newDob are rendered as ComparisonCard children
          (React nodes), so the casts preserve the exact runtime values. */}
      {hasDobChange && (
        <ComparisonCard
          title="Date of Birth Comparison"
          currentLabel="Current Date of Birth"
          newChangeLabel="New Date of Birth"
          current={currentDob as React.ReactNode}
          newChange={newDob as React.ReactNode}
          changes={dobChanges}
        />
      )}

      {hasAddressChange && (
        <ComparisonCard
          title="Address Comparison"
          currentLabel="Current Address"
          newChangeLabel="New Address"
          current={formatAddress(currentAddress)}
          newChange={formatAddress(newAddress)}
          changes={addressChanges}
        />
      )}
    </>
  );
};

export default withApollo<OwnProps>(UserKycProfileUpdateApproval);
